// Application Type in the URL — `?usertype=direct|crm|pp` on every page after the
// landing page.
//
// The landing page ('/') receives the Application Type through its (encrypted)
// query string and stores it in redux as `user.applicationType` ('DIRECT' |
// 'CRM' | 'PP', see helper/constant.js `userType`). From the OTP step onwards
// every navigation in this app is a bare route literal (`history.push('/dad/pan')`),
// so the type vanished from the address bar. Business wants it visible on every
// subsequent page, including after a vendor SDK round-trip (DigiLocker, eSign,
// OneMoney) — those return to a bare route AND reload the SPA, which wipes redux.
//
// This module is the single place that knows how to (a) work out the current
// type, (b) decorate a navigation target with it, and (c) repair the URL after a
// reload. It is wired in at exactly one navigation choke point — the singleton
// history in src/lib/history.js wraps push/replace with `withUserType` — so the
// ~430 call sites stay untouched. Nothing here renders or changes UI.
//
// Where the type comes from, in priority order (see resolveUserType):
//   1. the URL we are on right now (`?usertype=`)
//   2. sessionStorage 'usertype' — written on every navigation, at signup from
//      the type the signup body itself declares (quick-link PP/CRM, partner
//      code), and overwritten with the backend's value whenever
//      GET /registration/resume/{userId} answers. This is what survives the
//      full-page reload of a vendor return.
//   3. redux `user.applicationType` — the landing-link value (DIRECT default)
//   4. sessionStorage 'client_applicationType' — the legacy cookie-driven entry
//      (regOprnAccountFormWithOTPFunc); Phase D retires this fallback.
// If none of those know, the param is OMITTED rather than guessed: a CRM lead
// must never be labelled `direct` because we were unsure.
//
// The backend is authoritative. The lead's applicationType is exposed by
// GET /registration/resume/{userId} (ResumeStateResult.ApplicationType,
// anonymous) — NOT by GET /journey/status, whose DTO carries only stage/state.
// On a resume via the landing page (mobile → OTP re-auth) the landing link may
// not carry the lead's real type — a CRM customer re-entering through the plain
// DIY link, or through a Zoho RM /continue/{code} link for a lead still at OTP,
// used to be labelled `direct` all the way through the OTP page (EW-I7707).
// Since BE EW-I7707, POST /registration/signup answers with the created/resumed
// lead's `applicationType` (additive key); `userTypeAfterSignup` below prefers
// that, then the handoff prefill's type, then the body's own guess, so the very
// first post-landing URL is right. /registration/otp/verify still returns no
// applicationType; the /dad mount guard fires the resume lookup alongside its
// /journey/status call and `syncUserTypeInUrl` corrects the param if anything
// still disagrees.
import { parsePath, createPath } from 'history';
import { appPathname } from './homeUrl';

export const USER_TYPE_PARAM = 'usertype';

const STORAGE_KEY = 'usertype';
const LEGACY_STORAGE_KEY = 'client_applicationType';

// 'partner' is the value the DIY signup body sends for a partner-code
// registration (regOpenAccountFormFunc signupV3: PP → 'PARTNER'); the lead
// is franchisee either way, so it reads back as `pp`.
const VALUES = { direct: 'direct', crm: 'crm', pp: 'pp', partner: 'pp' };

// Routes that must stay bare. '/' is the landing page (its own query string is
// the encrypted prefill payload — never append to it). /continue/:shortCode is
// the public 2nd-device resume landing that hands off to '/' or into /dad/*.
const LANDING_PATHS = [/^\/?$/, /^\/continue(\/|$)/i];

let _store = null;

// Called once from src/index.js, next to services/journey registerStore, so the
// resolver can read redux without importing the store (which would be circular
// via lib/history → redux/store → lib/history).
export const registerUserTypeStore = (store) => {
    _store = store;
};

// 'DIRECT' | 'direct' | 'Crm' | 'PP' → 'direct' | 'crm' | 'pp'; anything else
// (alliance, '', null, garbage from a URL) → null.
export const normaliseUserType = (value) => {
    if (value == null) return null;
    const v = String(value).trim().toLowerCase();
    return VALUES[v] || null;
};

// sessionStorage can throw (Safari private mode, locked-down webviews). Never
// let that break a navigation.
const safeSession = (fn) => {
    try {
        return fn();
    } catch (e) {
        return null;
    }
};

export const rememberUserType = (value) => {
    const type = normaliseUserType(value);
    if (!type) return null;
    safeSession(() => sessionStorage.setItem(STORAGE_KEY, type));
    return type;
};

// New-journey hygiene: called from resetJourneyProgress() (top of
// POST /registration/signup) and resetJourneyState() so a CRM journey that ended
// in this tab cannot label the next DIY journey.
export const clearRememberedUserType = () => {
    safeSession(() => sessionStorage.removeItem(STORAGE_KEY));
};

// Which Application Type to remember right after POST /registration/signup
// answers (EW-I7707). The signup body's `applicationType` is only the FE's
// guess: a DAD-shaped body legitimately RESUMES an existing CRM/PP lead (the
// BE's cross-vertical rule lets DIRECT resume any vertical), and that is exactly
// what a Zoho RM "Resume eKYC" link does for a lead still at OTP — so labelling
// the OTP page from the body said `direct` for a CRM customer. Priority:
//   1. `response.applicationType` — the BE's answer, the lead's real vertical
//      (additive key on CreateLeadResult; absent on older backends → skipped)
//   2. `handoffType` — the /continue/{code} resolve prefill's applicationType,
//      which the Continue page stored in redux (also read off the leads row)
//   3. `payloadType` — the body's own value, unchanged behaviour for every
//      flow that is not a resume.
// Returns the raw value (rememberUserType normalises) or null when nothing is
// usable — omit rather than guess, same rule as resolveUserType.
export const userTypeAfterSignup = ({ response, handoffType, payloadType } = {}) => {
    const fromResponse = response && normaliseUserType(response.applicationType) ? response.applicationType : null;
    if (fromResponse) return fromResponse;
    if (normaliseUserType(handoffType)) return handoffType;
    return normaliseUserType(payloadType) ? payloadType : null;
};

const rememberedUserType = () => normaliseUserType(safeSession(() => sessionStorage.getItem(STORAGE_KEY)));
const legacyUserType = () => normaliseUserType(safeSession(() => sessionStorage.getItem(LEGACY_STORAGE_KEY)));
const reduxUserType = () => {
    if (!_store) return null;
    const state = _store.getState();
    return normaliseUserType(state && state.user && state.user.applicationType);
};

const currentSearch = () => (typeof window !== 'undefined' && window.location ? window.location.search || '' : '');
const currentHash = () => (typeof window !== 'undefined' && window.location ? window.location.hash || '' : '');

const stripQuestionMark = (search) => String(search || '').replace(/^\?/, '');

// Raw `key=value` pairs, untouched. We deliberately do NOT round-trip through
// URLSearchParams.toString() when rebuilding a search string — that re-encodes
// every other param ('+' vs '%20', pre-encoded values) and vendors' callback
// params (accessRequestId, s, …) must reach their handlers byte-for-byte.
const pairs = (search) => stripQuestionMark(search).split('&').filter(Boolean);
const isUserTypePair = (pair) => decodeURIComponent(pair.split('=')[0]).toLowerCase() === USER_TYPE_PARAM;

export const hasUserTypeParam = (search) => pairs(search).some(isUserTypePair);

export const readUserTypeFromSearch = (search) => {
    const hit = pairs(search).find(isUserTypePair);
    if (!hit) return null;
    const idx = hit.indexOf('=');
    return normaliseUserType(idx === -1 ? '' : decodeURIComponent(hit.slice(idx + 1)));
};

// '?usertype=crm&s=1' → '?s=1'; '?usertype=crm' → ''; '' → ''.
export const searchWithoutUserType = (search) => {
    const kept = pairs(search).filter((pair) => !isUserTypePair(pair));
    return kept.length ? `?${kept.join('&')}` : '';
};

const appendUserType = (search, type) => {
    const base = searchWithoutUserType(search);
    return `${base}${base ? '&' : '?'}${USER_TYPE_PARAM}=${type}`;
};

export const shouldCarryUserType = (pathname) => {
    const p = pathname == null || pathname === '' ? '/' : String(pathname);
    return !LANDING_PATHS.some((re) => re.test(p));
};

export const resolveUserType = () => (
    readUserTypeFromSearch(currentSearch())
    || rememberedUserType()
    || reduxUserType()
    || legacyUserType()
);

// For the intentional query-strips (dadRejectionCorrection drops the DigiLocker
// callback params after consuming them): everything goes except usertype.
export const userTypeOnlySearch = () => {
    const type = resolveUserType();
    return type ? `?${USER_TYPE_PARAM}=${type}` : '';
};

// Decorate a navigation target. `to` is whatever history.push/replace accept:
// a string ('/dad/pan', '/dad/adhaar?accessRequestId=1&s=x#h') or a location
// object ({ pathname, search, hash, state }). Returns the same shape.
//   - landing paths → unchanged
//   - usertype already present (any case, any value) → unchanged. No duplicates,
//     and ordinary navigation never overrides a value that is already there —
//     only syncUserTypeInUrl (backend-authoritative) does that.
//   - no type resolvable → unchanged (omit, don't guess)
//   - otherwise append, preserving the existing search verbatim and the hash.
export const withUserType = (to, userType) => {
    if (to == null) return to;
    const isString = typeof to === 'string';
    const loc = isString ? parsePath(to) : to;
    // A location object with no pathname is a same-page update (search/hash
    // only) — judge eligibility by where we are.
    const pathname = loc.pathname != null ? loc.pathname : appPathname();
    if (!shouldCarryUserType(pathname)) return to;
    if (hasUserTypeParam(loc.search)) return to;
    const type = userType !== undefined ? normaliseUserType(userType) : resolveUserType();
    if (!type) return to;
    rememberUserType(type);
    const search = appendUserType(loc.search, type);
    return isString ? createPath({ ...loc, search }) : { ...loc, search };
};

// Repair the live URL after a full-page entry into the journey (vendor return,
// refresh, deep link). `authoritativeType` is the backend's value from
// GET /journey/status: when given it wins and CORRECTS a differing param; when
// null we only fill a missing param from resolveUserType(). Returns true if a
// history.replace was issued. Uses appPathname() (router coordinates) because
// the history has a basename and the raw pathname carries the deploy prefix.
export const syncUserTypeInUrl = (history, authoritativeType) => {
    if (!history || typeof window === 'undefined') return false;
    const pathname = appPathname();
    if (!shouldCarryUserType(pathname)) return false;

    const search = currentSearch();
    const inUrl = readUserTypeFromSearch(search);
    const wanted = normaliseUserType(authoritativeType);

    let type;
    if (wanted) {
        rememberUserType(wanted);
        if (inUrl === wanted) return false;
        type = wanted;
    } else {
        if (hasUserTypeParam(search)) return false;
        type = resolveUserType();
        if (!type) return false;
    }

    history.replace(`${pathname}${appendUserType(search, type)}${currentHash()}`);
    return true;
};

export const ensureUserTypeInUrl = (history) => syncUserTypeInUrl(history, null);
